import React, { useState, useEffect } from "react";

const CreateLoanModal = ({
  isCreateLoanModalOpen,
  closeCreateLoanModal,
  loanProductName,
  loanLimit,
  loanInterest,
  handleReviewLoanDetails,
}) => {
  const [loanAmount, setLoanAmount] = useState("");
  const [loanDuration, setLoanDuration] = useState("");
  const [repaymentInterval, setRepaymentInterval] = useState("Monthly");
  const [totalRepayment, setTotalRepayment] = useState(0);
  const [monthlyRepayment, setMonthlyRepayment] = useState(0);
  const [repaymentMonths, setRepaymentMonths] = useState(1);
  const [purpose, setPurpose] = useState("");
  const [error, setError] = useState("");

  const loanDurations = {
    "Flash Loan": ["1 Month"],
    "Emergency Loan": ["1 Month", "2 Months"],
    "Normal Loan": ["1 Month", "2 Months", "3 Months", "5 Months", "6 Months"],
    "Development Loan": ["6 Months", "12 Months", "24 Months", "36 Months"],
    "Jijenge Loan": [
      "1 Month",
      "2 Months",
      "3 Months",
      "4 Months",
      "5 Months",
      "6 Months",
    ],
  };

  useEffect(() => {
    const durations = loanDurations[loanProductName] || [];
    setLoanDuration(durations[0] || "");
    setError("");
  }, [loanProductName]);

  useEffect(() => {
    if (!isCreateLoanModalOpen) {
      setLoanAmount("");
      setLoanDuration(loanDurations[loanProductName]?.[0] || "");
      setError("");
      setPurpose("");
    }
  }, [isCreateLoanModalOpen]);

  useEffect(() => {
    if (!loanAmount || !loanDuration) return;

    const monthsMatch = loanDuration.match(/\d+/);
    const months = monthsMatch ? parseInt(monthsMatch[0]) : 1;
    setRepaymentMonths(months);

    const principal = parseFloat(loanAmount.replace(/,/g, "")) || 0;
    const interestRate = loanInterest < 1 ? loanInterest : loanInterest / 100;

    let balance = principal;
    const principalPayment = principal / months;
    let totalAmountPaid = 0;

    for (let month = 1; month <= months; month++) {
      const interest = balance * interestRate;
      let totalPayment = principalPayment + interest;

      if (month === months) {
        totalPayment = balance + interest;
      }

      totalAmountPaid += totalPayment;
      balance = month === months ? 0 : balance - principalPayment;
    }

    setTotalRepayment(totalAmountPaid.toFixed(2));
    setMonthlyRepayment((totalAmountPaid / months).toFixed(2));
  }, [loanAmount, loanDuration, loanInterest]);

  if (!isCreateLoanModalOpen) return null;

  const handleAmountChange = (e) => {
    const rawValue = e.target.value.replace(/[^\d.]/g, "");
    if (parseFloat(rawValue) > loanLimit) {
      setError(`Max Loan Amount: KES ${Number(loanLimit).toLocaleString()}`);
      return;
    }
    setError("");
    setLoanAmount(rawValue);
  };

  const handleContinue = () => {
    if (error || !loanAmount) return;

    const normalizedInterest =
      loanInterest < 1 ? loanInterest * 100 : loanInterest;

    handleReviewLoanDetails({
      amount: Number(loanAmount),
      monthlyRepayment: Number(monthlyRepayment),
      duration: loanDuration,
      interest: normalizedInterest,
      repaymentInterval,
      totalRepayment: Number(totalRepayment),
      // purpose,
    });
  };

  return (
    <div className="fixed inset-0 z-10 bg-black bg-opacity-50 flex items-center justify-center overflow-auto">
      <div className="relative flex items-center min-h-screen w-full">
        <div className="relative bg-white shadow w-[600px] md:w-[800px] h-[75vh] mx-auto rounded-sm flex flex-col">
          <div className="flex justify-end p-4 border-b">
            <button
              onClick={closeCreateLoanModal}
              className="text-gray-600 hover:text-gray-800 text-2xl"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-8 py-6 pr-10">
            <h2 className="text-[20px] font-semibold text-gray-900 mb-2">
              Create Loan – <span className="font-bold">{loanProductName}</span>
            </h2>

            <p className="text-gray-600 text-[14px] mb-6">
              Provide the loan parameters below to complete the creation
              process.
            </p>

            <div className="grid grid-cols-2 gap-x-10 gap-y-5 mb-[40px]">
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">
                  Loan Amount
                </label>
                <div>
                  <div className="flex">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-600">
                        KES
                      </div>
                      <input
                        type="text"
                        className={`block w-full pl-12 pr-3 py-2 border ${
                          error ? "border-red-500" : "border-gray-300"
                        } rounded-l-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-[#074073]`}
                        value={loanAmount}
                        onChange={handleAmountChange}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <p className="mt-2 text-[13px] text-red-600">
                    {error ||
                      `Max Loan Amount: KES ${Number(
                        loanLimit
                      ).toLocaleString()}`}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">
                  Select Loan Duration
                </label>
                <select
                  className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-sm bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-[#074073]"
                  value={loanDuration}
                  onChange={(e) => setLoanDuration(e.target.value)}
                >
                  {(loanDurations[loanProductName] || []).map((duration) => (
                    <option key={duration}>{duration}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">
                  Loan Interest (%)
                </label>
                <input
                  type="text"
                  className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-sm bg-gray-100 text-gray-900 cursor-not-allowed"
                  value={loanInterest < 1 ? loanInterest * 100 : loanInterest}
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">
                  Repayment Interval
                </label>
                <input
                  type="text"
                  className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-sm bg-gray-100 text-gray-900 cursor-not-allowed"
                  value={repaymentInterval}
                  readOnly
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-800 mb-1">
                  Purpose
                </label>
                <input
                  type="text"
                  className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-sm bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-[#074073]"
                  placeholder="Enter Loan Purpose"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                />
              </div>
            </div>

            <h3 className="text-sm font-semibold mb-2">Repayment Summary</h3>

            <div className="bg-[#dcecff] p-5 rounded-sm text-sm">
              <div className="flex justify-between mb-2">
                <span>Total Repayment</span>
                <strong>KES {Number(totalRepayment).toLocaleString()}</strong>
              </div>

              <div className="flex justify-between">
                <span>Monthly</span>
                <strong>
                  KES {Number(monthlyRepayment).toLocaleString()} ·{" "}
                  {repaymentMonths} Months
                </strong>
              </div>
            </div>
          </div>

          <div className="flex justify-between p-6 border-t">
            <button
              onClick={closeCreateLoanModal}
              className="border border-[#074073] text-[#074073] px-4 py-2 rounded-sm"
            >
              Cancel Application
            </button>

            <button
              onClick={handleContinue}
              disabled={!!error || !loanAmount}
              className={`px-6 py-2 rounded-sm text-white ${
                error
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#074073] hover:bg-[#052f5a]"
              }`}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateLoanModal;